import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { localizeCategoryType } from './connectorCategory.util';
import {
    type ConnectionKind,
    ModelConstants,
    Dataset,
    Metamodel,
    FormatDatasetV2,
    stringIsNullOrEmpty,
    format
} from 'shell-models-lib';
import { constructDatastoreDocLink } from './links.util';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { LinkedServiceUtils } from 'app/model/linkedservice/linked-service.utils';
import { IFrameService } from 'app/services/iframe.service';
import { LoggerService } from 'app/logger/logger.service';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { DataSourceOptionKind } from '@powerquery/mashup-editor-client';

export interface IConnectionInfo<V = string> {
    label: string;
    value: V;
    icon: string;
    category: string;
    docLink?: string;
}

export function generateConnectionDisplayInfo<T = string>(metadata: ILinkedServiceTypeMetadata): IConnectionInfo<T> {
    const connectionInfo: IConnectionInfo<T> = {
        label: metadata.displayName,
        value: metadata.type as T,
        icon: metadata.icon.className,
        category: localizeCategoryType(metadata.category[0]),
        docLink: constructDatastoreDocLink(metadata.type)
    };
    return connectionInfo;
}

export function getLinkedServiceTypeFromDatasetMetadataAndConnectionKind(
    dataset: Dataset,
    connectionKind: ConnectionKind,
    services: {
        iframeService: IFrameService;
        datasetRegistry: DatasetRegistryService;
    }
): string {
    const datasetType = Metamodel.modelOf(dataset).name;
    const metadata = services.datasetRegistry.getDatasetConfiguration(datasetType);
    // Delta declares only OneLake Files in `supportLinkedServiceTypes`, but a Delta clone copy writes it to any
    // clone-capable store, so that list would resolve every Delta dataset to OneLake Files. Use its location,
    // falling back to the shared resolution below for any dataset shape that does not carry a tagged one.
    if (datasetType === ModelConstants.deltaFormat && (dataset as FormatDatasetV2).location) {
        const location = (dataset as FormatDatasetV2).location;
        const locationLinkedServiceType = Metamodel.modelOf(location)?.tags()?.linkedServiceModel?.name;
        if (!stringIsNullOrEmpty(locationLinkedServiceType)) {
            return locationLinkedServiceType;
        }
    }
    if (metadata.supportLinkedServiceTypes?.length > 1 && !stringIsNullOrEmpty(connectionKind)) {
        if (metadata.datasetKind === 'file' && (dataset as FormatDatasetV2).location) {
            const location = (dataset as FormatDatasetV2).location;
            return Metamodel.modelOf(location)?.tags()?.linkedServiceModel?.name;
        } else {
            const linkedServiceTypes =
                services.iframeService.connectionKindToLinkedServiceTypesMapping.get(connectionKind);
            return metadata.supportLinkedServiceTypes.find((type) => linkedServiceTypes.includes(type));
        }
    } else {
        return metadata.supportLinkedServiceTypes?.[0];
    }
}

export function generateLsTypeOptionsForMappingToMultipleConnectors(
    connectionKind: ConnectionKind,
    isCopySink: boolean,
    ownerActivityType: string,
    services: {
        iframeService: IFrameService;
        ecSvc: ExposureControlService;
        logger: LoggerService;
        datasetRegistry: DatasetRegistryService;
        linkedServiceRegistry: LinkedServiceRegistryService;
    },
    callerName: string
): Array<ISelectOption<string>> {
    const { iframeService, ecSvc, logger, datasetRegistry, linkedServiceRegistry } = services;
    if (
        !iframeService.connectionKindToLinkedServiceTypesMapping.has(connectionKind) ||
        !iframeService.isMappingToMultipleConnectors(connectionKind)
    ) {
        return [];
    }
    const linkedServiceTypes = iframeService.connectionKindToLinkedServiceTypesMapping.get(connectionKind);
    logger.log(callerName, `Connection kind: ${connectionKind} - mapped linked services: ${linkedServiceTypes.join()}`);
    const metadataList = linkedServiceTypes.map((linkedServiceType) =>
        linkedServiceRegistry.getLinkedServiceConfiguration(linkedServiceType)
    );
    logger.log(callerName, `Linked service metadata list has: ${metadataList.length} items`);
    if (metadataList.length) {
        const isLookup = ownerActivityType === ModelConstants.lookupActivity;
        const isGetMetaData = ownerActivityType === ModelConstants.getMetadataActivity;
        const isDelete = ownerActivityType === ModelConstants.deleteActivity;
        const isCopyJob = ownerActivityType === ModelConstants.copyJobActivity;
        const linkedServiceToDatasetMetadataMap: Map<string, IDatasetTypeMetadata[]> = new Map<
            string,
            IDatasetTypeMetadata[]
        >();
        datasetRegistry.getAllPrimaryAndAvailableDatasetConfigurations().forEach((datasetMetadata) => {
            datasetMetadata.supportLinkedServiceTypes.forEach((linkedServiceType) => {
                if (linkedServiceToDatasetMetadataMap.has(linkedServiceType)) {
                    linkedServiceToDatasetMetadataMap.get(linkedServiceType).push(datasetMetadata);
                } else {
                    linkedServiceToDatasetMetadataMap.set(linkedServiceType, [datasetMetadata]);
                }
            });
        });
        return metadataList
            .filter((linkedServiceMetadata) => {
                logger.log(
                    callerName,
                    `Getting dataset metadata for linked service: ${linkedServiceMetadata.displayName}, ${linkedServiceMetadata.type}`
                );
                if (
                    linkedServiceMetadata.type ===
                        adfModelTypeMap.azureDataLakeStoreCosmosStructuredStream.linkedServiceType ||
                    linkedServiceMetadata.type ===
                        adfModelTypeMap.azureDataLakeStoreGen2CosmosStructuredStream.linkedServiceType
                ) {
                    logger.log(
                        callerName,
                        `Linked service type: ${
                            linkedServiceMetadata.type
                        }, isHidden: ${!ecSvc.enableADLSCosmosDataset}, actual flag: ${ecSvc.enableADLSCosmosDataset}`
                    );
                }
                if (isLookup) {
                    return !linkedServiceToDatasetMetadataMap
                        .get(linkedServiceMetadata.type)
                        .every((datasetMetadata) => datasetMetadata.notSupportLookupActivity);
                }
                if (isDelete) {
                    return linkedServiceToDatasetMetadataMap
                        .get(linkedServiceMetadata.type)
                        .some((datasetMetadata) => datasetMetadata.supportDeleteActivity);
                }
                if (isGetMetaData) {
                    return linkedServiceToDatasetMetadataMap
                        .get(linkedServiceMetadata.type)
                        .some((datasetMetadata) => datasetMetadata.supportGetMetadataActivity);
                }
                if (isCopyJob) {
                    const support = isCopySink
                        ? linkedServiceMetadata.supportCopyJobSink
                        : linkedServiceMetadata.supportCopyJobSource;

                    if (!support) {
                        return false;
                    }

                    return !haveStubConnector(linkedServiceMetadata, connectionKind);
                }
                const supported = LinkedServiceUtils.isSupportedLsForConnectionRole(
                    linkedServiceMetadata,
                    isCopySink ? 'sink' : 'source'
                );
                logger.log(
                    callerName,
                    `Dataset: ${linkedServiceToDatasetMetadataMap
                        .get(linkedServiceMetadata.type)
                        .map((datasetMetadata) => datasetMetadata.displayName)
                        .join(', ')}, ${supported} for linked service type: ${linkedServiceMetadata.type}`
                );
                return supported;
            })
            .map((metadata) => ({
                label: metadata?.inPreview === 'both' ||
                (metadata?.inPreview === 'source' && !isCopySink) ||
                (metadata?.inPreview === 'sink' && isCopySink) ?
                    format(stringResources.ModelName_DisplayTextPreviewTemplate, metadata?.displayName) : metadata.displayName,
                value: metadata.type,
                labelIcon: metadata.icon.className
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }
    return [];
}

export const haveStubConnector = (metadata: ILinkedServiceTypeMetadata, connectionKind: ConnectionKind): boolean => {
    if (metadata.pqoDataSourceOption == null) {
        return false;
    } else if (metadata.pqoDataSourceOption.kind === DataSourceOptionKind.BuiltIn) {
        return false;
    } else if (metadata.pqoDataSourceOption.kind === DataSourceOptionKind.Extension) {
        return metadata.pqoDataSourceOption.resourceKind !== connectionKind;
    }
    return false;
};

export function isSalesforceV2BasedLs(lsType: string): boolean {
    return lsType === adfModelTypeMap.salesforceV2.linkedServiceType || lsType === adfModelTypeMap.salesforceServiceCloudV2.linkedServiceType;
}

export function isShopifyV2BasedLs(lsType: string, lsVersion: string): boolean {
    return lsType === adfModelTypeMap.shopify.linkedServiceType && lsVersion === '2.0';
}

export function isHubspotV2BasedLs(lsType: string, lsVersion: string): boolean {
    return lsType === adfModelTypeMap.hubspot.linkedServiceType && lsVersion === '2.0';
}

export function isSquareV2BasedLs(lsType: string, lsVersion: string): boolean {
    return lsType === adfModelTypeMap.square.linkedServiceType && lsVersion === '2.0';
}

export function isXeroV2BasedLs(lsType: string, lsVersion: string): boolean {
    return lsType === adfModelTypeMap.xero.linkedServiceType && lsVersion === '2.0';
}

export function isJiraV2BasedLs(lsType: string, lsVersion: string): boolean {
    return lsType === adfModelTypeMap.jira.linkedServiceType && lsVersion === '2.0';
}